import { z } from "zod";
import { addressFields, addressTypes } from "../core/address";
import { validators } from "../core/primitives";
import { DAYS_OF_WEEK } from "@convex/schemas/validators/dockHours.lib";
import { LIMITED_ACCESS_KIND, SITE_INSIDE, SITE_PLACE } from "@convex/constants/siteAccess.lib";
import type { FreightVehicle } from "@convex/schemas/validators/freightShipment.lib";
import { literalValues } from "@shared/validation/literal-values";

export const siteAccessSchema = z.object({
  dock: z.boolean().optional(),
  forklift: z.boolean().optional(),
  palletJack: z.boolean().optional(),
  place: z.enum(literalValues(SITE_PLACE)).optional(),
  limitedAccessKind: z.enum(literalValues(LIMITED_ACCESS_KIND)).optional(),
  appointment: z.boolean().optional(),
  inside: z.enum(literalValues(SITE_INSIDE)).optional(),
  driverAssist: z.boolean().optional(),
  dropTrailer: z.boolean().optional(),
  lumper: z.boolean().optional(),
  vehicles: z.array(z.custom<FreightVehicle>((value) => typeof value === "string")).optional(),
});

export const dockHourEntrySchema = z.object({
  day: z.enum(DAYS_OF_WEEK),
  opens: z.string(),
  closes: z.string(),
});

export const addressDialogSchema = z.object({
  addressType: z.enum(addressTypes),
  addressTypeLabel: z.string().optional(),
  label: z.string().optional(),
  line1: addressFields.line1,
  line2: z.string().optional(),
  city: addressFields.city,
  state: validators.state.required(),
  postalCode: validators.postalCode.required(),
  country: z.string().default("US"),
  isDefault: z.boolean().default(false),
  dockHours: z.array(dockHourEntrySchema).optional(),
  siteAccess: siteAccessSchema,
  notes: z.string().max(500).optional(),
});

export type AddressDialogFormData = z.infer<typeof addressDialogSchema>;

export type AddressDialogFormInput = z.input<typeof addressDialogSchema>;

export const addressInlineSchema = addressDialogSchema.pick({
  addressType: true,
  addressTypeLabel: true,
  label: true,
  line1: true,
  line2: true,
  city: true,
  state: true,
  postalCode: true,
  country: true,
  isDefault: true,
});

export type AddressInlineFormData = z.infer<typeof addressInlineSchema>;

export const addressDialogDefaults: AddressDialogFormData = {
  addressType: "shipping",
  addressTypeLabel: "",
  label: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
  isDefault: false,
  dockHours: [],
  siteAccess: {},
  notes: "",
};
